import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HEADER_MARGIN_TOP_PX } from "src/components/layout/header/constants";

export type HeaderHeightContextType = {
  headerHeight: number;
  headerElementRef: (element: HTMLDivElement | null) => void;
};

const defaultContextValue = {
  headerHeight: 0,
  headerElementRef: () => {},
};

export const HeaderHeightContext =
  createContext<HeaderHeightContextType>(defaultContextValue);
HeaderHeightContext.displayName = "HeaderHeightContext";

export const useHeaderHeight = () => useContext(HeaderHeightContext);

interface Props {
  children: ReactNode;
}

export const HeaderHeightProvider: FC<Props> = ({ children }) => {
  const [headerHeight, setHeaderHeight] = useState(0);
  const [headerElement, setHeaderElement] = useState<HTMLDivElement | null>(
    null,
  );
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Callback ref function that React will call when element is attached/detached
  const headerElementRef = useCallback((element: HTMLDivElement | null) => {
    setHeaderElement(element);
  }, []);

  // Set up ResizeObserver when element changes
  useEffect(() => {
    // Clean up existing observer
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    if (!headerElement) {
      setHeaderHeight(0);
      return;
    }

    // Create and set up new ResizeObserver
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setHeaderHeight(
          entry.contentRect.height > 0
            ? // Add banner spacing from the top of the viewport
              entry.contentRect.height + HEADER_MARGIN_TOP_PX
            : 0,
        );
      }
    });

    resizeObserver.observe(headerElement);
    resizeObserverRef.current = resizeObserver;

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [headerElement]);

  const value = useMemo(
    () => ({ headerHeight, headerElementRef }),
    [headerHeight, headerElementRef],
  );

  return (
    <HeaderHeightContext.Provider value={value}>
      {children}
    </HeaderHeightContext.Provider>
  );
};
